import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import EodOCSubBar from '../components/eodoc/EodOCSubBar';
import EodOCCharts from '../components/eodoc/EodOCCharts';
import EodSpotTable from '../components/eodoc/EodSpotTable';
import OCTables from '../components/liveoc/OCTables';
import { eodOcApi } from '../utils/eodOcApi';
import { toChartSeries, toSpotTableRows, toTableRows } from '../utils/eodOcTransform';
import '../styles/live-oc.css';

export default function EodOCPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [exch, setExch] = useState('NSE');
  const [tp, setTp] = useState('IDO');
  const [symbols, setSymbols] = useState([]);
  const [dates, setDates] = useState([]);
  const [expiries, setExpiries] = useState([]);
  const [symbol, setSymbol] = useState(searchParams.get('symbol') || '');
  const [date, setDate] = useState(searchParams.get('date') || '');
  const [expiry, setExpiry] = useState(searchParams.get('expiry') || '');
  const [trendDays, setTrendDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [error, setError] = useState(null);

  const syncParams = useCallback((sym, dt, exp) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (sym) p.set('symbol', sym); else p.delete('symbol');
      if (dt) p.set('date', dt); else p.delete('date');
      if (exp) p.set('expiry', exp); else p.delete('expiry');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const loadChain = useCallback(async (filters, days = trendDays) => {
    const { exch: e, tp: t, symbol: sym, date: dt, expiry: exp } = filters;
    if (!sym || !dt || !exp) return;
    setLoading(true);
    setError(null);
    try {
      const res = await eodOcApi.load({ exch: e, sym, tp: t, dt, exp, days });
      if (!res.top6CE?.length) throw new Error('No option chain data for this selection');
      setData(res);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [trendDays]);

  const applyInit = useCallback(async (nextExch, nextTp, preferred = {}) => {
    setInitLoading(true);
    setError(null);
    try {
      const res = await eodOcApi.init(nextExch, nextTp);
      setSymbols(res.symbols || []);
      setDates(res.dates || []);
      setExpiries(res.expiries || []);

      const sym = preferred.symbol && res.symbols?.includes(preferred.symbol)
        ? preferred.symbol
        : res.defaults?.sym || res.symbols?.[0] || '';
      const dt = preferred.date && res.dates?.includes(preferred.date)
        ? preferred.date
        : res.defaults?.dt || res.dates?.[0] || '';
      const exp = preferred.expiry && res.expiries?.includes(preferred.expiry)
        ? preferred.expiry
        : res.defaults?.exp || res.expiries?.[0] || '';

      setSymbol(sym);
      setDate(dt);
      setExpiry(exp);
      syncParams(sym, dt, exp);

      if (sym && dt && exp) {
        await loadChain({ exch: nextExch, tp: nextTp, symbol: sym, date: dt, expiry: exp });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setInitLoading(false);
    }
  }, [loadChain, syncParams]);

  useEffect(() => {
    applyInit(exch, tp, {
      symbol: searchParams.get('symbol') || undefined,
      date: searchParams.get('date') || undefined,
      expiry: searchParams.get('expiry') || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.querySelector('.tmr-main')?.classList.add('oc-pg');
    return () => document.querySelector('.tmr-main')?.classList.remove('oc-pg');
  }, []);

  const onExchChange = async (nextExch) => {
    setExch(nextExch);
    setData(null);
    await applyInit(nextExch, tp);
  };

  const onTpChange = async (nextTp) => {
    setTp(nextTp);
    setData(null);
    await applyInit(exch, nextTp);
  };

  const onSymbolChange = async (sym) => {
    if (!sym) return;
    setSymbol(sym);
    setLoading(true);
    setError(null);
    try {
      const res = await eodOcApi.switchSymbol(exch, sym, tp);
      setDates(res.dates || []);
      setExpiries(res.expiries || []);
      const dt = res.dt || '';
      const exp = res.exp || '';
      setDate(dt);
      setExpiry(exp);
      syncParams(sym, dt, exp);
      if (dt && exp) await loadChain({ exch, tp, symbol: sym, date: dt, expiry: exp });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onDateChange = async (dt) => {
    if (!dt || !symbol) return;
    setDate(dt);
    setLoading(true);
    setError(null);
    try {
      const res = await eodOcApi.expiries(exch, symbol, tp, dt);
      setExpiries(res.expiries || []);
      const exp = res.exp || '';
      setExpiry(exp);
      syncParams(symbol, dt, exp);
      if (exp) await loadChain({ exch, tp, symbol, date: dt, expiry: exp });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onExpiryChange = async (exp) => {
    setExpiry(exp);
    syncParams(symbol, date, exp);
    if (symbol && date && exp) await loadChain({ exch, tp, symbol, date, expiry: exp });
  };

  const onTrendDaysChange = (days) => {
    setTrendDays(days);
    if (symbol && date && expiry) loadChain({ exch, tp, symbol, date, expiry }, days);
  };

  const tableRows = useMemo(() => toTableRows(data), [data]);
  const chartSeries = useMemo(() => (data ? toChartSeries(data) : null), [data]);
  const spotRows = useMemo(() => toSpotTableRows(data), [data]);
  const showContent = data?.top6CE?.length > 0;

  return (
    <div className="oc-page flex flex-col h-full min-h-0">
      <EodOCSubBar
        exchanges={['NSE', 'BSE']}
        exch={exch}
        tp={tp}
        symbols={symbols}
        dates={dates}
        expiries={expiries}
        symbol={symbol}
        date={date}
        expiry={expiry}
        loading={loading || initLoading}
        summary={data?.summary}
        onExchChange={onExchChange}
        onTpChange={onTpChange}
        onSymbolChange={onSymbolChange}
        onDateChange={onDateChange}
        onExpiryChange={onExpiryChange}
        onLoad={() => loadChain({ exch, tp, symbol, date, expiry: expiry })}
      />

      <div className="oc-g" id="ocGrid">
        {initLoading && (
          <div className="oc-loading" style={{ display: 'flex' }}>
            <div className="oc-spin" /> Loading EOD option chain…
          </div>
        )}

        {!initLoading && loading && !showContent && (
          <div className="oc-loading" style={{ display: 'flex' }}>
            <div className="oc-spin" /> Loading…
          </div>
        )}

        {!initLoading && !loading && error && !showContent && (
          <div className="oc-empty">
            <i className="bi bi-x-circle" style={{ fontSize: 18, color: 'var(--red)' }} /> {error}
          </div>
        )}

        {!initLoading && !showContent && !loading && !error && (
          <div className="oc-empty">
            <i className="bi bi-layout-three-columns" style={{ fontSize: 20 }} /> Select filters and click LOAD
          </div>
        )}

        {showContent && (
          <>
            <OCTables ceRows={tableRows.ceRows} peRows={tableRows.peRows} />
            <EodOCCharts series={chartSeries} trendDays={trendDays} onTrendDaysChange={onTrendDaysChange} />
            <EodSpotTable rows={spotRows} />
          </>
        )}
      </div>
    </div>
  );
}